import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { CsvSection } from "../csvExport";
import { formatDisplayDate } from "../formatters";
import { BrandStagesDrill, useBrandStagesData, useBrandStagesDrillDown } from "../hooks/useBrandStagesData";
import {
  ALL_BRANDS_UID,
  BRAND_STAGES_MATRIX_CSV_HEADERS,
  BrandStagesBrand,
  BrandStagesRange,
  getDefaultBrandStagesRange,
  mapLeadFunnelRows,
  toBrandStagesMatrixCsvRow,
} from "./brandStagesApiMapping";
import BrandStagesFilterCard from "./BrandStagesFilterCard";
import BrandStagesKpiCards from "./BrandStagesKpiCards";
import BrandStagesFunnel from "./BrandStagesFunnel";
import BrandStagesMatrix, { BrandStagesMatrixColumn } from "./BrandStagesMatrix";
import BrandStagesDrillDownDrawer from "./BrandStagesDrillDown/BrandStagesDrillDownDrawer";

interface BrandStagesProps {
  userToken: any;
  websiteSharedUid: string;
}

// Besides the export every tab exposes, Brand Stages exposes refresh() for
// the page header's Refresh button: unlike the other tabs it can't just be
// remounted, since its applied range isn't URL-synced and would reset to
// quarter-to-date.
export interface BrandStagesHandle {
  getExportSections: () => CsvSection[] | null;
  refresh: () => void;
}

function toQueryRange([start, end]: [Dayjs, Dayjs]): BrandStagesRange {
  return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
}

const BrandStages = forwardRef<BrandStagesHandle, BrandStagesProps>(function BrandStages(
  { userToken, websiteSharedUid },
  ref
) {
  const {
    brandStagesAllFunnelData,
    brandStagesAllFunnelLoading,
    brandStagesAllFunnelError,
    brandStagesBrandsData,
    brandStagesBrandFunnelsData,
    brandStagesMatrixLoading,
    brandStagesMatrixError,
  } = useSelector((state: any) => state.optifundDashboard);

  const [initialRange] = useState(getDefaultBrandStagesRange);
  const [appliedRange, setAppliedRange] = useState<BrandStagesRange>(() => toQueryRange(initialRange));
  // Bumped by GET DATA / Refresh so the same range still refetches.
  const [fetchToken, setFetchToken] = useState(0);
  const [drill, setDrill] = useState<BrandStagesDrill | null>(null);

  const { retryAllFunnel, retryMatrix } = useBrandStagesData(userToken, websiteSharedUid, appliedRange, fetchToken);
  const drillDown = useBrandStagesDrillDown(userToken, websiteSharedUid, appliedRange, drill);

  const rangeLabel = `${formatDisplayDate(appliedRange.start)} – ${formatDisplayDate(appliedRange.end)}`;

  const allCounts = useMemo(
    () => (Array.isArray(brandStagesAllFunnelData) ? mapLeadFunnelRows(brandStagesAllFunnelData) : null),
    [brandStagesAllFunnelData]
  );
  const allFunnelLoading =
    brandStagesAllFunnelLoading || (!Array.isArray(brandStagesAllFunnelData) && !brandStagesAllFunnelError);

  const brands: BrandStagesBrand[] | null = Array.isArray(brandStagesBrandsData) ? brandStagesBrandsData : null;
  const matrixLoading = brandStagesMatrixLoading || (brands === null && !brandStagesMatrixError);

  // Brand columns sorted by paid count desc. A brand whose own Lead Funnel
  // call failed is left out of the matrix and named in its error banner.
  const { matrixColumns, failedBrandNames } = useMemo(() => {
    if (!brands) return { matrixColumns: [] as BrandStagesMatrixColumn[], failedBrandNames: null };
    const columns: BrandStagesMatrixColumn[] = [];
    const failed: string[] = [];
    brands.forEach((brand) => {
      const rows = brandStagesBrandFunnelsData?.[brand.brandUid];
      if (Array.isArray(rows)) columns.push({ brand, counts: mapLeadFunnelRows(rows) });
      else failed.push(brand.brandName);
    });
    columns.sort((a, b) => b.counts[0] - a.counts[0] || a.brand.brandName.localeCompare(b.brand.brandName));
    return { matrixColumns: columns, failedBrandNames: failed };
  }, [brands, brandStagesBrandFunnelsData]);

  const handleGetData = (range: [Dayjs, Dayjs]) => {
    setAppliedRange(toQueryRange(range));
    setFetchToken((token) => token + 1);
  };

  const matrixReady = !matrixLoading && !brandStagesMatrixError && brands !== null;

  useImperativeHandle(
    ref,
    () => ({
      getExportSections: (): CsvSection[] | null => {
        if (!matrixReady) return null;
        return [
          {
            title: `Brand Stages - Stages by Brand - reported ${rangeLabel}`,
            headers: BRAND_STAGES_MATRIX_CSV_HEADERS,
            rows: matrixColumns.map((column) => toBrandStagesMatrixCsvRow(column.brand.brandName, column.counts)),
          },
        ];
      },
      refresh: () => setFetchToken((token) => token + 1),
    }),
    [matrixReady, matrixColumns, rangeLabel]
  );

  return (
    <Box>
      <BrandStagesFilterCard initialRange={initialRange} onGetData={handleGetData} />

      <Typography sx={{ mt: 3.25, mb: 0.5, fontSize: 20, fontWeight: 500, color: "#1a1a1a" }}>
        Brand Stages Overview
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#9e9e9e", mb: 1.75 }}>
        Hover a stage for its numbers. Click a stage to open the leads that reached it.
      </Typography>
      <BrandStagesKpiCards
        counts={allFunnelLoading || brandStagesAllFunnelError ? null : allCounts}
        brandCount={matrixLoading || !brands ? null : brands.length}
      />

      <BrandStagesFunnel
        counts={allCounts}
        isLoading={allFunnelLoading}
        isError={brandStagesAllFunnelError}
        onRetry={retryAllFunnel}
        rangeLabel={rangeLabel}
        onStageClick={(stageIndex) => setDrill({ brandUid: ALL_BRANDS_UID, brandName: null, stageIndex })}
      />

      <BrandStagesMatrix
        columns={matrixColumns}
        failedBrandNames={failedBrandNames}
        isLoading={matrixLoading}
        isError={brandStagesMatrixError}
        onRetry={retryMatrix}
        rangeLabel={rangeLabel}
        onCellClick={(brand, stageIndex) =>
          setDrill({ brandUid: brand.brandUid, brandName: brand.brandName, stageIndex })
        }
      />

      <BrandStagesDrillDownDrawer
        drill={drill}
        rows={drillDown.rows}
        isError={drillDown.isError}
        onRetry={drillDown.retry}
        onClose={() => setDrill(null)}
        range={appliedRange}
        rangeLabel={rangeLabel}
      />
    </Box>
  );
});

export default BrandStages;
